import React from "react";
import {
  FaCalendarAlt,
  FaCheck,
  FaCopy,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaShareAltSquare,
} from "react-icons/fa";

import {
  DEFAULT_EVENT_IMAGE,
  formatEventDate,
  formatEventDay,
  formatEventMonth,
  formatEventYear,
  getEventCategories,
  getEventDepartment,
  getEventStatusClass,
  getEventStatusLabel,
} from "../utils/sharedEventsUtils";

function EventMeta({ icon: Icon, label, value }) {
  if (!value) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 shrink-0 text-sky-700 dark:text-sky-200" />
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-sm font-bold text-slate-800 dark:text-white">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

function SharedEventCard({
  event,
  isCopying = false,
  isCopied = false,
  copyDisabled = false,
  onCopy,
  onViewDetails,
}) {
  const categories = getEventCategories(event);
  const department = getEventDepartment(event);

  return (
    <article className="group overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 transition hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="relative min-h-56 overflow-hidden bg-slate-950">
        <img
          src={event?.photo_url || DEFAULT_EVENT_IMAGE}
          alt={event?.titre || "Événement partagé"}
          className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 group-hover:scale-105"
          onError={(eventImage) => {
            eventImage.currentTarget.src = DEFAULT_EVENT_IMAGE;
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

        <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-xl">
          <div className="bg-gradient-to-br from-blue-600 to-orange-500 px-4 py-2 text-white">
            <p className="text-3xl font-bold leading-none">
              {formatEventDay(event?.start)}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
              {formatEventMonth(event?.start)}
            </p>
          </div>
          <p className="px-3 py-1 text-xs font-bold text-slate-500">
            {formatEventYear(event?.start)}
          </p>
        </div>

        <div className="absolute right-4 top-4 flex flex-wrap justify-end gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold text-white backdrop-blur-xl">
            <FaShareAltSquare />
            Partagé
          </span>

          <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${getEventStatusClass(
              event,
            )}`}
          >
            {getEventStatusLabel(event)}
          </span>
        </div>

        <div className="absolute bottom-4 left-4 right-4 text-white">
          

          <h3 className="mt-3 line-clamp-2 font-titre text-2xl font-bold leading-tight">
            {event?.titre || "Événement sans titre"}
          </h3>
        </div>
      </div>

      <div className="p-5">
        <p className="line-clamp-3 text-sm font-medium leading-7 text-slate-600 dark:text-white/55">
          {event?.description || "Aucune description disponible."}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {event?.type_event && (
            <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
              {event.type_event}
            </span>
          )}

          {event?.level_event && (
            <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
              {event.level_event}
            </span>
          )}

          {department && (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
              Département {department}
            </span>
          )}

          {categories.map((category) => (
            <span
              key={category}
              className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60"
            >
              {category}
            </span>
          ))}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <EventMeta
            icon={FaMapMarkerAlt}
            label="Lieu"
            value={event?.lieu_event || "Non spécifié"}
          />

          <EventMeta
            icon={FaCalendarAlt}
            label="Horaires"
            value={event?.horaire_event || "Non spécifié"}
          />
        </div>

        <div className="mt-5 grid gap-2 border-t border-slate-200 pt-5 dark:border-white/10 sm:grid-cols-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={isCopying}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
          >
            {isCopied ? <FaCheck /> : <FaCopy />}
            {isCopying ? "Copie..." : isCopied ? "Copié" : "Copier"}
          </button>

          <button
            type="button"
            onClick={onViewDetails}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-emerald-200 hover:text-emerald-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-emerald-300/40 dark:hover:text-emerald-100"
          >
            <FaInfoCircle />
            Détails
          </button>
        </div>
      </div>
    </article>
  );
}

export default SharedEventCard;